import { FC, useEffect, useState } from "react";
import { UseFormReturn } from "react-hook-form";
import { makeStyles } from "tss-react/mui";
import {
  Image,
  MiAutocomplete,
  MiDetailFields,
  MiFileUpload,
  MiModalConfirm,
  MiSectionHeader,
} from "@miplatform/components";
import {
  BUTTON_COLORS,
  BUTTON_LABELS,
  FILE_UPLOAD_VARIANTS,
  INPUT_TYPES,
  MODAL_TITLES,
  VALID_IMAGE_FILE_MIME_TYPES,
} from "@miplatform/constants";
import { useRouter } from "@miplatform/hooks";
import { mdiPlus, mdiTrashCanOutline } from "@mdi/js";
import { Box, TextField } from "@mui/material";
import { ConfigAccordionHeader } from "../../../../../Config/components/ConfigAccordionHeader/ConfigAccordionHeader";
import { IItemAttribute, IItemData, IItemDocument } from "../../Items.types";
import { useItemDetailTabViewModel } from "./useItemDetailTabViewModel";

export const ItemDetailTab: FC<ItemDetailTabProps> = ({
  form,
  extraTabProps = {},
}: ItemDetailTabProps) => {
  const [openChangeCategoryModal, setOpenChangeCategoryModal] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);

  const { getValues, setValue, control, reset, watch } = form;
  const { isReadOnly } = extraTabProps;
  const { params } = useRouter();
  const { id: itemId } = params;
  const { classes, theme } = useStyles();

  const {
    isLoading,
    category,
    categoryOptions,
    attributeOptions,
    imageRef,
    setCategory,
    fetchCategoryDetails,
    getCategoryOptions,
    getAttributeOptions,
    deleteImage,
    handleUploadImage,
    handleUpdateImage,
    getAutoGenerate,
  } = useItemDetailTabViewModel({ setValue, watch });

  const {
    document = {} as IItemDocument,
    category: { categoryId, categoryName, imageRequired } = {},
  } = watch();
  const { id: documentId } = document;

  useEffect(() => {
    getCategoryOptions();
  }, []);

  useEffect(() => {
    if (categoryId) {
      fetchCategoryDetails(categoryId);
    }
  }, [categoryId]);

  const handleResetFields = () => {
    setCategory([]);
    reset();
    setOpenChangeCategoryModal(false);
  };

  const handleItemCategory = (value: { value: string; label: string }) => {
    const { value: id, label } = value;
    setValue(`category.categoryName`, label);
    setValue(`category.categoryId`, id);
    getAutoGenerate(getValues());
  };

  const handleResetCategory = () => {
    if (categoryName) {
      setOpenChangeCategoryModal(true);
    }
  };

  const commonDisabled = !categoryId || isReadOnly;

  const attributeFields = (attr: IItemAttribute, index: number) => [
    {
      fieldType: INPUT_TYPES.AUTOCOMPLETE,
      name: `attributes[${index}].value`,
      formControl: control,
      label: attr.name,
      freeSolo: true,
      options: attributeOptions[attr.id] || [],
      disableClearable: true,
      forcePopupIcon: true,
      required: attr.isRequired,
      disabled: commonDisabled,
      width: "100%",
      onChange: () => getAutoGenerate(getValues()),
      textFieldOnChange: (e: React.ChangeEvent<HTMLInputElement>) => {
        setValue(`attributes.${index}.value`, e.target.value);
        getAttributeOptions(attr.id, categoryId as string, e.target.value);
        getAutoGenerate(getValues());
      },
      textFieldOnFocus: () => {
        setValue(`attributes.${index}.attributeId`, attr.id);
        getAttributeOptions(attr.id, categoryId as string);
      },
      handleOnChange: (_: unknown, value: { label: string }) => {
        setValue(`attributes.${index}.value`, value.label);
      },
    },
  ];

  const itemDetailFields = [
    {
      label: "Part Number",
      name: "manufacturerPartNumber",
      disabled: commonDisabled,
      formControl: control,
      required: true,
      onBlur: () => getAutoGenerate(getValues()),
    },
    {
      label: "Part Description",
      name: "manufacturerDescription",
      disabled: commonDisabled,
      formControl: control,
      required: true,
    },
    {
      label: "Field Note",
      name: "fieldNote",
      disabled: commonDisabled,
      formControl: control,
      width: "100%",
    },
  ];

  return (
    <>
      <ConfigAccordionHeader title="Attach Category">
        <MiSectionHeader
          subtitle="Pick from an existing category to add your item. This selection will impact the fields required for your item."
          variant="text"
        />
        <MiAutocomplete
          options={categoryOptions}
          inputValue={categoryName || ""}
          onChange={(_, value) => handleItemCategory(value)}
          disabled={!!itemId}
          disableClearable
          renderInput={(params) => (
            <TextField
              {...params}
              required={!isReadOnly}
              disabled={!!itemId}
              label={`${itemId ? "" : "Select"} Category`}
              size="small"
              onClick={!itemId ? handleResetCategory : undefined}
            />
          )}
        />
      </ConfigAccordionHeader>
      <ConfigAccordionHeader title="Attached Attributes">
        <MiSectionHeader
          subtitle="Decide what kind of attributes attach to this category."
          variant="text"
        />
        {category.attributes?.map((attr: IItemAttribute, index: number) => (
          <MiDetailFields
            key={attr.id}
            detailFields={attributeFields(attr, index)}
          />
        ))}
      </ConfigAccordionHeader>
      <ConfigAccordionHeader title="Enter Item Details">
        <MiSectionHeader
          subtitle="Add the details of your item below."
          variant="text"
        />
        <MiDetailFields detailFields={itemDetailFields} />
        <Box className={classes.imageUploadContainer}>
          {documentId ? (
            <Box className={classes.imageWrapper}>
              <Image
                image={document}
                containerClass={classes.imageContainer}
                isEditing={!isReadOnly}
                onEdit={
                  categoryId ? () => imageRef.current?.click() : undefined
                }
                onDelete={
                  !imageRequired ? () => setOpenDeleteModal(true) : undefined
                }
                fileName={document?.title || document.metadata?.title}
              />
            </Box>
          ) : (
            <MiFileUpload
              dragAndDrop
              acceptableFormatText="Acceptable formats: png, jpg"
              variant={FILE_UPLOAD_VARIANTS.TEXT}
              showUploadButton
              buttonStartIcon={{ path: mdiPlus }}
              fullWidth
              title="Upload Image"
              browseFilesText={
                <Box>
                  upload image
                  {imageRequired && (
                    <span className={classes.requiredAsterisk}>*</span>
                  )}
                </Box>
              }
              validFileTypes={VALID_IMAGE_FILE_MIME_TYPES}
              onChange={(files: FileList) => {
                if (files.length > 0) {
                  handleUploadImage(files[0]);
                }
              }}
              acceptFiles="image/*"
              disabled={commonDisabled}
            />
          )}
          <input
            ref={imageRef}
            type="file"
            data-testid="profile-picture-input"
            accept="image/*"
            onChange={(e) => handleUpdateImage(e)}
            className={classes.imageInput}
          />
        </Box>
      </ConfigAccordionHeader>
      <MiModalConfirm
        title={MODAL_TITLES.DELETE}
        textPositive={BUTTON_LABELS.DELETE}
        buttonPositiveColor={BUTTON_COLORS.RED}
        open={openDeleteModal}
        handleNegative={() => setOpenDeleteModal(false)}
        handlePositive={() => {
          deleteImage();
          setOpenDeleteModal(false);
        }}
        titleIcon={{
          path: mdiTrashCanOutline,
          color: theme.palette.red.main,
        }}
        description="You are about to delete the image from this item. Are you sure you want to delete this image?"
        disableClick={isLoading}
      />
      <MiModalConfirm
        title="Change Item Category?"
        textPositive="Continue"
        open={openChangeCategoryModal}
        handleNegative={() => setOpenChangeCategoryModal(false)}
        handlePositive={handleResetFields}
        description="Are you sure you want to change item category? Previous Data will be deleted."
      />
    </>
  );
};

const useStyles = makeStyles()((theme) => ({
  imageContainer: {
    minWidth: 200,
    minHeight: 200,
    maxWidth: 200,
    maxHeight: 200,
    margin: theme.spacing(1, 0, 0, 1),
    borderRadius: 6,
  },
  imageUploadContainer: {
    display: "flex",
    margin: theme.spacing(1),
  },
  imageWrapper: {
    border: `1px dashed ${theme.palette.grey.accent}`,
    width: "100%",
    borderRadius: 4,
    display: "flex",
    flexDirection: "column",
  },
  imageInput: {
    display: "none",
  },
  requiredAsterisk: {
    color: theme.palette.red.main,
  },
}));

interface ItemDetailTabProps {
  form: UseFormReturn<IItemData>;
  extraTabProps?: {
    isReadOnly?: boolean;
  };
}
